import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { PATH_INFO } from "../utils/helper"
import { UpdateSamplesContext, SamplesResponseType } from "../../context/samplesContext"
import { useTableContext } from "../../context/tableContext"
import { buildAnnotatedImage, extractFileNameFromHeader, PREVIEW_MAX_WIDTH } from "./helpers"
import type {
  MeasurementReportGroup,
  MeasurementReportGroupMeta,
  MeasurementReportGroupResponse,
  MeasurementReportPreviewResponse,
  PreviewState,
  SampleRequestEntry
} from "./types"

const DEFAULT_GROUP_META: MeasurementReportGroupMeta = {
  total: 0,
  page: 1,
  limit: 50,
  hasMore: false,
  nextPage: null,
}

const GROUPS_PAGE_LIMIT = 50

type UseSpecificationDownloadResult = {
  sampleList: MeasurementReportGroup[]
  selectedGroupKeys: string[]
  previewMap: Record<string, PreviewState>
  sortOrder: "desc" | "asc"
  changeSortOrder: (order: "desc" | "asc") => void
  toggleGroupSelection: (groupKey: string) => void
  handleDownload: () => Promise<void>
  isDownloading: boolean
  isDownloadDisabled: boolean
  errorModal: ErrorModalState
  closeErrorModal: () => void
  groupsMeta: MeasurementReportGroupMeta
  loadMoreGroups: () => Promise<void>
}

type ErrorModalState = {
  isOpen: boolean
  message: string
}

export function useSpecificationDownload(): UseSpecificationDownloadResult {
  const navigate = useNavigate()
  const { idSearchSample } = useContext(UpdateSamplesContext)
  const { getColor, getDisplayIndex } = useTableContext()
  const [sampleList, setSampleList] = useState<MeasurementReportGroup[]>([])
  const [selectedGroupKeys, setSelectedGroupKeys] = useState<string[]>([])
  const [previewMap, setPreviewMap] = useState<Record<string, PreviewState>>({})
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc")
  const [isDownloading, setIsDownloading] = useState(false)
  const [groupsMeta, setGroupsMeta] = useState<MeasurementReportGroupMeta>(DEFAULT_GROUP_META)
  const groupsMetaRef = useRef<MeasurementReportGroupMeta>(DEFAULT_GROUP_META)
  const [isGroupsLoading, setIsGroupsLoading] = useState(false)
  const [errorModal, setErrorModal] = useState<ErrorModalState>({
    isOpen: false,
    message: ""
  })
  const previewMapRef = useRef(previewMap)
  const previewRequestRef = useRef<Record<string, symbol>>({})
  const isMountedRef = useRef(true)
  const sampleDetailCache = useRef<Record<string, SamplesResponseType>>({})

  useEffect(() => {
    groupsMetaRef.current = groupsMeta
  }, [groupsMeta])

  const closeErrorModal = useCallback(() => {
    setErrorModal(prev => ({ ...prev, isOpen: false }))
  }, [])

  const openErrorModal = useCallback((message: string) => {
    setErrorModal({ isOpen: true, message })
  }, [])

  const getSampleDetailWithCache = useCallback(
    async (sampleId: string): Promise<SamplesResponseType | undefined> => {
      if (!sampleId) {
        return undefined
      }
      if (sampleDetailCache.current[sampleId]) {
        return sampleDetailCache.current[sampleId]
      }
      if (!idSearchSample) {
        return undefined
      }
      try {
        const fetched = await idSearchSample(sampleId, true, false)
        if (fetched && typeof fetched === "object") {
          const normalized = fetched as SamplesResponseType
          sampleDetailCache.current[sampleId] = normalized
          return normalized
        }
      } catch (error) {
        console.error("サンプル詳細の取得に失敗しました。", error)
      }
      return undefined
    },
    [idSearchSample]
  )

  const fetchMeasurementReportGroups = useCallback(async (options?: { append?: boolean }) => {
    const shouldAppend = options?.append ?? false
    const meta = groupsMetaRef.current
    const targetPage = shouldAppend ? (meta.nextPage ?? meta.page + 1) : 1
    if (shouldAppend && !meta.hasMore) {
      return
    }
    try {
      setIsGroupsLoading(true)
      const params = new URLSearchParams({
        page: String(targetPage),
        limit: String(GROUPS_PAGE_LIMIT),
      })

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/measurement-reports/groups?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("token")}`
        }
      })

      if (res.status === 401 || res.status === 403) {
        sessionStorage.removeItem("token")
        navigate(PATH_INFO.LOGIN.PATH)
        return
      }

      if (!res.ok) {
        console.error("listMeasurementReportGroups の取得に失敗しました。")
        return
      }

      const data: MeasurementReportGroupResponse = await res.json()
      const items = data.items

      let mergedList: MeasurementReportGroup[] = []
      setSampleList(prev => {
        const base = shouldAppend ? prev : []
        const map = new Map<string, MeasurementReportGroup>()
        base.forEach(item => {
          map.set(item.group_key, item)
        })
        items.forEach(item => {
          map.set(item.group_key, item)
        })
        mergedList = Array.from(map.values())
        return mergedList
      })

      setGroupsMeta(data.meta)

      const finalList = mergedList
      setSelectedGroupKeys(prev => prev.filter(key => finalList.some(item => item.group_key === key)))
      setPreviewMap(prev => {
        const next: Record<string, PreviewState> = {}
        finalList.forEach(item => {
          if (prev[item.group_key]) {
            next[item.group_key] = prev[item.group_key]
          }
        })
        return next
      })
    } catch (error) {
      console.error("listMeasurementReportGroups の取得中にエラーが発生しました。", error)
    } finally {
      setIsGroupsLoading(false)
    }
  }, [navigate])

  const loadMoreMeasurementReportGroups = useCallback(async () => {
    if (isGroupsLoading || !groupsMetaRef.current.hasMore) {
      return
    }
    await fetchMeasurementReportGroups({ append: true })
  }, [fetchMeasurementReportGroups, isGroupsLoading])

  useEffect(() => {
    previewMapRef.current = previewMap
  }, [previewMap])

  useEffect(() => {
    fetchMeasurementReportGroups()
  }, [fetchMeasurementReportGroups])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const loadPreview = async (groupKey: string, group: MeasurementReportGroup) => {
      if (!group.representative_sample_id) {
        if (isMountedRef.current) {
          setPreviewMap(prev => ({
            ...prev,
            [groupKey]: { status: "error", errorMessage: "代表サンプルIDを取得できませんでした。" }
          }))
        }
        return
      }

      const current = previewMapRef.current[groupKey]
      if (current && current.status === "success") {
        return
      }

      if (isMountedRef.current) {
        setPreviewMap(prev => ({
          ...prev,
          [groupKey]: { status: "loading" }
        }))
      }
      const requestId = Symbol("preview-request")
      previewRequestRef.current[groupKey] = requestId

      try {
        const sampleDetail = await getSampleDetailWithCache(group.representative_sample_id)
        if (!isMountedRef.current || previewRequestRef.current[groupKey] !== requestId) {
          return
        }

        let annotatedImage: string | null = null
        if (sampleDetail && typeof window !== "undefined") {
          try {
            annotatedImage = await buildAnnotatedImage(sampleDetail, getColor, getDisplayIndex)
          } catch (imageError) {
            console.error("サンプル画像の描画に失敗しました。", imageError)
          }

          if (!annotatedImage) {
            if (isMountedRef.current && previewRequestRef.current[groupKey] === requestId) {
              setPreviewMap(prev => ({
                ...prev,
                [groupKey]: { status: "error", errorMessage: "採寸画像がありません。" }
              }))
            }
            return
          }
        }

        const payload: Record<string, unknown> = {
          sample_id: group.representative_sample_id,
          preview: { maxWidth: PREVIEW_MAX_WIDTH }
        }
        if (annotatedImage) {
          payload.sampleImage = annotatedImage
        }

        const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/measurement-reports/preview`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${sessionStorage.getItem("token")}`
          },
          body: JSON.stringify(payload)
        })

        if (res.status === 401 || res.status === 403) {
          sessionStorage.removeItem("token")
          if (isMountedRef.current && previewRequestRef.current[groupKey] === requestId) {
            navigate(PATH_INFO.LOGIN.PATH)
          }
          return
        }

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`)
        }

        const data = (await res.json()) as MeasurementReportPreviewResponse
        if (!isMountedRef.current || previewRequestRef.current[groupKey] !== requestId) {
          return
        }

        setPreviewMap(prev => ({
          ...prev,
          [groupKey]: { status: "success", data }
        }))
      } catch (error) {
        if (!isMountedRef.current || previewRequestRef.current[groupKey] !== requestId) {
          return
        }
        if ((error as Error).name === "AbortError") {
          return
        }
        console.error("/measurement-reports/preview の取得中にエラーが発生しました。", error)
        setPreviewMap(prev => ({
          ...prev,
          [groupKey]: { status: "error", errorMessage: "プレビュー取得に失敗しました。" }
        }))
      }
    }

    selectedGroupKeys.forEach(groupKey => {
      const group = sampleList.find(item => item.group_key === groupKey)
      if (!group) {
        return
      }
      const current = previewMapRef.current[groupKey]
      if (current && (current.status === "loading" || current.status === "success")) {
        return
      }
      loadPreview(groupKey, group)
    })
  }, [selectedGroupKeys, sampleList, getSampleDetailWithCache, getColor, getDisplayIndex, navigate])

  const toggleGroupSelection = useCallback((groupKey: string) => {
    setSelectedGroupKeys(prev => {
      if (prev.includes(groupKey)) {
        return prev.filter(key => key !== groupKey)
      }
      return [...prev, groupKey]
    })
  }, [])

  const handleDownload = useCallback(async () => {
    if (isDownloading) {
      return
    }
    if (selectedGroupKeys.length === 0) {
      openErrorModal("ダウンロード対象が選択されていません。")
      return
    }
    const token = sessionStorage.getItem("token")
    if (!token) {
      navigate(PATH_INFO.LOGIN.PATH)
      return
    }

    const representativeSampleIds = Array.from(
      new Set(
        selectedGroupKeys.map(groupKey => {
            const target = sampleList.find(item => item.group_key === groupKey)
            if (target && typeof target.representative_sample_id === "string") {
              return target.representative_sample_id.trim()
            }
            return ""
        }).filter((id): id is string => id.length > 0)
      )
    )

    if (representativeSampleIds.length === 0) {
      openErrorModal("選択されたグループに有効なサンプルがありません。")
      return
    }

    setIsDownloading(true)
    try {
      const sampleRequests = await Promise.all(
        representativeSampleIds.map(async sampleId => {
          const requestEntry: SampleRequestEntry = {
            sample_id: sampleId
          }

          if (typeof window !== "undefined") {
            try {
              const detail = await getSampleDetailWithCache(sampleId)
              if (detail) {
                const annotated = await buildAnnotatedImage(detail, getColor, getDisplayIndex)
                if (annotated) {
                  requestEntry.sampleImage = annotated
                }
              }
            } catch (imageError) {
              console.error(`サンプルID ${sampleId} の描画に失敗しました。`, imageError)
            }
          }

          return requestEntry
        })
      )

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/client/measurement-reports/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ sample_requests: sampleRequests })
      })

      if (res.status === 401 || res.status === 403) {
        sessionStorage.removeItem("token")
        navigate(PATH_INFO.LOGIN.PATH)
        return
      }

      if (!res.ok) {
        await res.text().catch(() => "")
        openErrorModal("ダウンロードに失敗しました。時間をおいて再度お試しください。")
        return
      }

      const blob = await res.blob()
      const dispositionHeader = res.headers.get("Content-Disposition")
      const resolvedFileName =
        extractFileNameFromHeader(dispositionHeader) ??
        (representativeSampleIds.length === 1 ? "measurement-report.pdf" : "measurement-reports.zip")

      const downloadUrl = window.URL.createObjectURL(blob)
      const anchor = document.createElement("a")
      anchor.href = downloadUrl
      anchor.download = resolvedFileName
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error("exportMeasurementReportBulk の実行時にエラーが発生しました。", error)
      openErrorModal("ダウンロード処理中にエラーが発生しました。時間をおいて再度お試しください。")
    } finally {
      setIsDownloading(false)
    }
  }, [getDisplayIndex, getColor, getSampleDetailWithCache, isDownloading, navigate, openErrorModal, sampleList, selectedGroupKeys])

  const changeSortOrder = useCallback((order: "desc" | "asc") => {
    setSortOrder(order)
  }, [])

  const isDownloadDisabled = useMemo(
    () => selectedGroupKeys.length === 0 || isDownloading,
    [isDownloading, selectedGroupKeys.length]
  )

  return {
    sampleList,
    selectedGroupKeys,
    previewMap,
    sortOrder,
    changeSortOrder,
    toggleGroupSelection,
    handleDownload,
    isDownloading,
    isDownloadDisabled,
    errorModal,
    closeErrorModal,
    groupsMeta,
    loadMoreGroups: loadMoreMeasurementReportGroups,
  }
}
